import React from 'react'
import {partial} from 'lodash'
import 'lightgallery/css/lightgallery.css'
import 'lightgallery/css/lg-zoom.css'
import 'lightgallery/css/lg-thumbnail.css'
import {useSearchParam} from 'react-use'
import {NetworkError, NetworkErrorBoundary, useController, useSuspense} from '@data-client/react'
import {PublicTripResource} from '../lib/model'
import {PageProps} from '../components/PageRouter'
import {TripNav, TripView} from '../components/Trip'
import {Avatar, Box, Button, Container, Typography} from '@mui/material'
import {IconInfo, IconSearch} from '../Icons'
import {AppMsg, Field, SubmitButton} from '../components'
import {PublicTripForm} from '../lib/form/Trip'
import {useForm, useToggle} from '../lib'
import {PublicTripSchema} from '../lib/schema/Trip'

function TripNotFound(id: string, {error}: {error: NetworkError}) {
  const form = useForm(PublicTripSchema, {defaults: {happened: null, tags: []}})

  /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
  /* @ts-expect-error */
  const onSubmit = form.handleSubmit(async (data: PublicTripSchema) => {
    window.location.replace(`/trip/${id}?token=${data.token}`)
  })

  return (
    <Container maxWidth="md">
      <AppMsg severity="error">Trip Not Found or Invalid Token</AppMsg>
      <form onSubmit={onSubmit}>
        <Box my={5}>
          <Field form={form} attrs={PublicTripForm.token} />
        </Box>
        <Box mt={3} textAlign="center">
          <SubmitButton form={form}>View Trip</SubmitButton>
        </Box>
      </form>
    </Container>
  )
}

interface TripFoundProps {
  id: number
  token: string | null
}

function TripFound({id, token}: TripFoundProps) {
  const api = useController()
  const trip = useSuspense(PublicTripResource.get, {id, token})
  React.useEffect(() => {
    // TODO: this kludge will also invalidate trips that actually have no fotos
    if (trip.fotos.length === 0 && trip.children.length === 0) {
      try {
        api.invalidate(PublicTripResource.get, {id, token})
      } catch {
        // ignore for now since this useEffect is a temporary workaround
      }
    }
  }, [api, id, token, trip.fotos.length, trip.children.length])
  const search = useToggle()
  return (
    <TripView trip={trip}>
      <Container maxWidth="md" sx={{position: 'relative'}}>
        <Box className="app-flex" flexWrap="nowrap" mt="10px" bgcolor="rgba(50, 50, 50, 0.5)" borderRadius={1}>
          <Typography className="app-flex" variant="h3" color="secondary" p={1}>
            {trip.user && (
                <Avatar
                  alt={trip.user.name}
                  title={trip.user.name}
                  src={trip.user.avatar}
                  sx={{mr: 2, width: 50, height: 50, boxShadow: '4px 2px 6px #999'}}
                />
            )}
            {trip.title}
          </Typography>
          <Box className="app-flex" mr={3}>
            <Button title="Trip Details" href={`/trip/${trip.id}-${trip.slug}/`} color="primary" variant="contained" size="small">
              <IconInfo />
            </Button>
            &nbsp;
            <Button title="Other Public Trips" onClick={search.toggle} color="primary" variant="contained" size="small">
              <IconSearch />
            </Button>
          </Box>
        </Box>
        {search.enabled && <TripNav isPublic={true} />}
      </Container>
    </TripView>
  )
}

export default function PublicTripPage({params}: PageProps) {
  const token = useSearchParam('token') || localStorage.getItem('trip.token')
  React.useEffect(() => {
    if (token) {
      localStorage.setItem('trip.token', token)
    }
  }, [token])
  return (
    <NetworkErrorBoundary fallbackComponent={partial(TripNotFound, params['id'])}>
      <TripFound id={Number(params['id'])} token={token} />
    </NetworkErrorBoundary>
  )
}
